import { memo, type CSSProperties } from 'react';

import { getPinnedOffset } from '../../core/pinned-offset';
import type { DataGridDensity, RowData, DataGridRow as TanStackRow } from '../../types';
import { cellContentVariants, cellVariants, groupRowVariants } from '../data-grid.variants';
import { Button, Checkbox, cn } from '../shared';

// The one column whose cell a band's checkbox belongs in, so it lines up with the checkboxes that
// column already holds in the header and in every data row.
const SELECT_COLUMN_ID = 'select';

/**
 * Whatever the accessor returned, as a label. Only a primitive names a group — an object would
 * read as `[object Object]` in the band and in the toggle's accessible name, so it names nothing.
 */
function labelOf(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

/**
 * The band's own select-all control. One optional object rather than a pair of optional props, so
 * "there is a checkbox" and "here is what it does" cannot be passed apart from each other.
 */
export interface GroupRowSelection {
  /** `indeterminate` is a third state, not a flavour of unchecked: some of the group is selected. */
  checked: boolean | 'indeterminate';
  onChange: (selected: boolean) => void;
}

export interface DataGridGroupRowProps<TRow extends RowData> {
  row: TanStackRow<TRow>;
  ariaRowIndex: number;
  /** Visible columns, their pinning, and the pinned ones' widths — the same memo token every row
   * takes. A band's cells carry sticky offsets too, so it needs the widths for the same reason. */
  columnLayoutKey: string;
  /** One `<td>` per visible column, so pinned offsets still apply. */
  columnIds: string[];
  /** The true size of the group. Absent, the band counts the rows it was handed. */
  count: number;
  expanded: boolean;
  onToggle: () => void;
  /**
   * Omitted — the default — the band shows no checkbox, which is the shape every grid had before
   * group selection existed. Present, it selects or clears the whole group.
   */
  selection?: GroupRowSelection;
  density?: DataGridDensity;
}

/**
 * A group band: one real `<td>` per column, sharing a background with no internal borders.
 *
 * Not a single `<td colSpan>`. A spanning cell has no per-column cell to carry `position: sticky`
 * and the offsets `pinned-offset.ts` computes, so the band would scroll sideways while the pinned
 * columns stayed. Keeping every cell real means pinning works for any column, pinned today or
 * pinned later, with no special case to remember.
 *
 * The label sits in the first cell that isn't holding the band's checkbox, and is allowed to
 * overflow into its empty neighbours, which is what makes N cells read as one band.
 */
function DataGridGroupRowImpl<TRow extends RowData>({
  row,
  ariaRowIndex,
  columnIds,
  count,
  expanded,
  onToggle,
  selection,
  density,
}: DataGridGroupRowProps<TRow>) {
  const label = labelOf(row.groupingValue);
  // A group keyed on a null or object value has no name to speak; the toggle still needs one.
  const name = label === '' ? 'group' : label;

  // The checkbox goes in the select column's own cell, which is what aligns it with that column
  // rather than leaving it to land near it — and the band's content then starts in the next cell.
  // With no checkbox, nothing moves: the content stays in the first cell, as it always was.
  const checkboxColumnId =
    selection === undefined
      ? undefined
      : columnIds.find((columnId) => columnId === SELECT_COLUMN_ID);
  // `?? columnIds[0]` is the degenerate grid whose only visible column *is* the checkbox column:
  // one cell then carries both, which is the pre-selection layout rather than a band with no name.
  const contentColumnId =
    columnIds.find((columnId) => columnId !== checkboxColumnId) ?? columnIds[0];

  return (
    <tr aria-rowindex={ariaRowIndex} className={groupRowVariants()}>
      {columnIds.map((columnId) => {
        const holdsCheckbox = columnId === checkboxColumnId;
        const holdsContent = columnId === contentColumnId;
        // Read per cell, exactly as `data-grid-cell.tsx` reads it: a band cell that stayed static
        // while the pinned columns beside it stuck slid out from under its own column the moment
        // the grid was scrolled sideways — which is most visible on the checkbox, since it is the
        // one band cell with something in it that has to line up with the rows below.
        const pinned = row.table.getColumn(columnId)?.getIsPinned() ?? false;
        const pinnedOffset = getPinnedOffset(row.table, columnId, pinned);
        const style: CSSProperties = {
          ...(pinnedOffset === undefined
            ? {}
            : { [pinned === 'start' ? 'left' : 'right']: pinnedOffset }),
          // Only the cell holding the label needs to spill; the rest exist so the pinned columns
          // keep a cell to stick to.
          ...(holdsContent ? { overflow: 'visible', whiteSpace: 'nowrap' } : {}),
        };

        return (
          <td
            key={columnId}
            style={style}
            // `variant: 'list'` whatever the grid's own variant is: a band is one surface, and
            // `grid`'s vertical rules would cut the label into column-shaped pieces.
            //
            // `bg-muted` after the variants, not instead of them: a sticky cell has to paint its
            // own background, and `cellVariants` paints a *data* row's (`bg-card`) — the band's
            // ground is the one `groupRowVariants` puts on the `<tr>`. The hover half of that pair
            // is overridden too: a band is not hoverable, so a pinned band cell that kept
            // `group-hover:bg-accent` would light up alone inside an unchanged band.
            className={cn(
              cellVariants({
                density,
                variant: 'list',
                align: holdsCheckbox && !holdsContent ? 'center' : 'left',
                pinned,
              }),
              pinned === false ? undefined : 'bg-muted group-hover:bg-muted',
            )}
          >
            {holdsCheckbox || holdsContent ? (
              <span
                // The same content box a control cell uses, from the same place, so the checkbox
                // is centred in its column by the rule that centres every other one — not by this
                // column's width happening to equal a checkbox plus its padding.
                className={cn(
                  cellContentVariants({
                    wrap: 'control',
                    align: holdsCheckbox && !holdsContent ? 'center' : 'left',
                  }),
                  holdsContent ? 'gap-inline' : undefined,
                )}
                // Indented by depth so a second grouping level is additive rather than a rewrite.
                {...(holdsContent ? { style: { paddingLeft: row.depth * 16 } } : {})}
              >
                {holdsCheckbox ? (
                  <Checkbox
                    checked={selection?.checked ?? false}
                    // `onCheckedChange`, not the `onClick` the row/header checkboxes use: those
                    // hand the event to a TanStack handler, while this one already knows the next
                    // value — and a click from `indeterminate` reports `true`, which is wanted.
                    onCheckedChange={(checked) => {
                      selection?.onChange(checked === true);
                    }}
                    aria-label={`Select all rows in ${name}`}
                  />
                ) : null}
                {holdsContent ? (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      icon={expanded ? 'chevron-down' : 'chevron-right'}
                      // The disclosure state belongs on the button: the contract forbids
                      // `role="grid"`, and `aria-expanded` on a `<tr>` outside a grid or treegrid
                      // context is not in a position where it is supported.
                      aria-expanded={expanded}
                      aria-label={expanded ? `Collapse ${name}` : `Expand ${name}`}
                      className="size-icon-lg p-0 hover:bg-transparent"
                      onClick={onToggle}
                    />
                    <span className="text-body font-medium">{label}</span>
                    <span className="text-caption text-muted-foreground">({count})</span>
                  </>
                ) : null}
              </span>
            ) : null}
          </td>
        );
      })}
    </tr>
  );
}

// A band changes for far less than a data row: it has no value of its own, so only its identity,
// its size, whether it is open, the column layout, and — where the band carries a checkbox — that
// checkbox's own tri-state can move it.
//
// `selection` is compared by `checked` rather than by reference: the body builds a fresh object
// (and a fresh `onChange` closure) every render, so a reference check would re-render every band
// on every render and defeat this memo entirely. Reading through `?.` also covers the band gaining
// or losing its checkbox, since `undefined` never equals a real tri-state.
function propsAreEqual<TRow extends RowData>(
  prev: DataGridGroupRowProps<TRow>,
  next: DataGridGroupRowProps<TRow>,
): boolean {
  return (
    prev.row === next.row &&
    prev.ariaRowIndex === next.ariaRowIndex &&
    prev.columnLayoutKey === next.columnLayoutKey &&
    prev.count === next.count &&
    prev.expanded === next.expanded &&
    prev.selection?.checked === next.selection?.checked &&
    prev.density === next.density
  );
}

export const DataGridGroupRow = memo(
  DataGridGroupRowImpl,
  propsAreEqual,
) as typeof DataGridGroupRowImpl;
